import React from 'react';
import { motion } from 'framer-motion';

export function HexLogo({ size = 40, animate = false }) {
  const s = size;
  const hexPath = (cx, cy, r) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    return `M${pts.join('L')}Z`;
  };

  const Component = animate ? motion.svg : 'svg';
  const animProps = animate ? {
    animate: { rotate: [0, 360] },
    transition: { duration: 60, repeat: Infinity, ease: 'linear' },
  } : {};

  return (
    <Component
      width={s}
      height={s}
      viewBox="0 0 100 100"
      fill="none"
      {...animProps}
    >
      <defs>
        <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3E7562" />
          <stop offset="100%" stopColor="#549E84" />
        </linearGradient>
      </defs>
      <path d={hexPath(35, 30, 22)} stroke="url(#hexGrad)" strokeWidth="2.5" fill="none" opacity="0.9" />
      <path d={hexPath(65, 30, 22)} stroke="url(#hexGrad)" strokeWidth="2.5" fill="none" opacity="0.7" />
      <path d={hexPath(50, 60, 22)} stroke="url(#hexGrad)" strokeWidth="2.5" fill="none" opacity="0.8" />
    </Component>
  );
}

export function HexPattern({ count = 6, style = {} }) {
  return (
    <div style={{ position: 'absolute', pointerEvents: 'none', ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            top: `${(i * 120) % 400}px`,
            left: `${(i * 80 + 20) % 300}px`,
            opacity: 0.04 + (i % 3) * 0.02,
          }}
          animate={{
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 6 + i * 0.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.8,
          }}
        >
          <HexShape size={40 + i * 15} />
        </motion.div>
      ))}
    </div>
  );
}

export function HexShape({ size = 60, color = '#3E7562', opacity = 0.15, filled = false }) {
  const hexPath = (cx, cy, r) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    return `M${pts.join('L')}Z`;
  };

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <path
        d={hexPath(50, 50, 45)}
        stroke={color}
        strokeWidth="1.5"
        fill={filled ? color : 'none'}
        opacity={opacity}
      />
    </svg>
  );
}

export function FloatingHexagons() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {[
        { x: '5%', y: '15%', size: 80, delay: 0, dur: 8 },
        { x: '85%', y: '10%', size: 60, delay: 1.5, dur: 7 },
        { x: '75%', y: '60%', size: 100, delay: 0.8, dur: 9 },
        { x: '10%', y: '70%', size: 50, delay: 2, dur: 6 },
        { x: '45%', y: '85%', size: 70, delay: 1, dur: 8 },
        { x: '90%', y: '40%', size: 45, delay: 3, dur: 7 },
      ].map((h, i) => (
        <motion.div
          key={i}
          style={{ position: 'absolute', left: h.x, top: h.y }}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 10, 0],
            opacity: [0.03, 0.07, 0.03],
          }}
          transition={{
            duration: h.dur,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: h.delay,
          }}
        >
          <HexShape size={h.size} color="#3E7562" opacity={0.08} />
        </motion.div>
      ))}
    </div>
  );
}

export function HexDivider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '48px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(62, 117, 98, 0.15)' }} />
      <HexShape size={24} color="#3E7562" opacity={0.3} />
      <div style={{ flex: 1, height: 1, background: 'rgba(62, 117, 98, 0.15)' }} />
    </div>
  );
}
